import { normalizePublicPath, normalizePublicPathPattern, } from "../../core/index.js";
const DefaultSitemapCacheControl = "public, max-age=3600, stale-if-error=86400";
const XmlContentType = "application/xml; charset=utf-8";
const TextContentType = "text/plain; charset=utf-8";
function compileDocumentPath(pathPattern) {
    const normalizedPattern = normalizePublicPathPattern(pathPattern);
    const placeholder = ":name";
    const placeholderIndex = normalizedPattern.indexOf(placeholder);
    if (placeholderIndex < 0
        || normalizedPattern.indexOf(placeholder, placeholderIndex + placeholder.length) >= 0)
        throw new Error("documentsPath must contain exactly one ':name' placeholder.");
    const prefix = normalizedPattern.slice(0, placeholderIndex);
    const suffix = normalizedPattern.slice(placeholderIndex + placeholder.length);
    return (pathname) => {
        if (!pathname.startsWith(prefix) || !pathname.endsWith(suffix))
            return {
                matched: false,
                name: null,
            };
        const endIndex = pathname.length - suffix.length;
        const name = pathname.slice(prefix.length, endIndex);
        return {
            matched: true,
            name: /^[a-z0-9][a-z0-9-]*$/.test(name) ? name : null,
        };
    };
}
function createResponse(statusCode, body, contentType, cacheControl) {
    return {
        statusCode,
        headers: {
            "Content-Type": contentType,
            "Cache-Control": cacheControl,
        },
        body,
    };
}
export function createPublicSitemapHandler(options) {
    const indexPath = normalizePublicPath(options.indexPath ?? "/sitemap.xml");
    const matchDocumentPath = compileDocumentPath(options.documentsPath ?? "/sitemaps/:name.xml");
    const cacheControl = options.cacheControl ?? DefaultSitemapCacheControl;
    return async (request) => {
        const pathname = normalizePublicPath(request.pathname);
        const documentMatch = matchDocumentPath(pathname);
        if (pathname !== indexPath && !documentMatch.matched)
            return null;
        const method = request.method.toUpperCase();
        if (method !== "GET" && method !== "HEAD")
            return {
                ...createResponse(405, "Method not allowed", TextContentType, "no-store"),
                headers: {
                    "Content-Type": TextContentType,
                    "Cache-Control": "no-store",
                    Allow: "GET, HEAD",
                },
                body: method === "HEAD" ? "" : "Method not allowed",
            };
        try {
            if (pathname !== indexPath && documentMatch.name === null)
                return createResponse(404, "", TextContentType, "no-store");
            const xml = pathname === indexPath
                ? await options.sitemap.renderIndex()
                : await options.sitemap.renderDocument(documentMatch.name);
            if (xml === null)
                return createResponse(404, "", TextContentType, "no-store");
            return createResponse(200, method === "HEAD" ? "" : xml, XmlContentType, cacheControl);
        }
        catch (error) {
            options.onError?.(error);
            return createResponse(503, method === "HEAD" ? "" : "Sitemap unavailable", TextContentType, "no-store");
        }
    };
}
export function createPublicSitemapFetchHandler(options) {
    const handler = createPublicSitemapHandler(options);
    return async (request) => {
        const response = await handler({
            method: request.method,
            pathname: new URL(request.url).pathname,
        });
        return response
            ? new Response(response.body, {
                status: response.statusCode,
                headers: response.headers,
            })
            : null;
    };
}
export function createExpressPublicSitemapMiddleware(options) {
    const handler = createPublicSitemapHandler(options);
    return async (request, response, next) => {
        try {
            const result = await handler({
                method: request.method,
                pathname: request.path,
            });
            if (!result) {
                next();
                return;
            }
            response.status(result.statusCode);
            for (const [name, value] of Object.entries(result.headers))
                response.setHeader(name, value);
            if (request.method.toUpperCase() === "HEAD" || result.body.length === 0)
                response.end();
            else
                response.send(result.body);
        }
        catch (error) {
            next(error);
        }
    };
}
//# sourceMappingURL=sitemap.js.map